import { useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { Loader2, Download, BookOpen } from "lucide-react";
import { StoreBadges } from "./StoreBadges";
import { useLang } from "@/i18n/LanguageContext";
import { buildStyleGuide, buildLookbook } from "./leadPdfs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [busyLookbook, setBusyLookbook] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter`, { email, lang });
      setStatus("success");
      setFeedback(t.news.success);
      setUnlocked(true);
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        setStatus("success");
        setFeedback(t.news.duplicate);
        setUnlocked(true);
      } else {
        setStatus("error");
        setFeedback(t.news.error);
      }
    }
  };

  const downloadLookbook = async () => {
    setBusyLookbook(true);
    try {
      let articles = [];
      try {
        const r = await axios.get(`${API}/blog/articles?lang=${lang}`);
        const arts = Array.isArray(r.data) ? r.data : r.data.articles || [];
        articles = arts.filter((a) => a.category !== "B2B");
      } catch (e) {
        articles = [];
      }
      buildLookbook(t, articles);
    } finally {
      setBusyLookbook(false);
    }
  };

  return (
    <section id="download" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-36" data-testid="newsletter-section">
      <Reveal>
        <div
          className="relative overflow-hidden rounded-[32px] border px-7 py-16 sm:px-16 sm:py-20 text-center"
          style={{ borderColor: "var(--gold)", background: "var(--surface)" }}
        >
          <div
            className="pointer-events-none absolute -top-24 left-1/2 h-[380px] w-[380px] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
            style={{ background: "radial-gradient(circle, var(--rose-soft) 0%, transparent 65%)" }}
          />
          <div className="relative">
            <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> {t.news.kicker}
            </p>
            <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="newsletter-title">
              {t.news.title}
            </h2>
            <p className="mx-auto mt-5 max-w-md text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>
              {t.news.text}
            </p>

            <div className="mt-10 flex justify-center">
              <StoreBadges height={52} />
            </div>

            <p className="mx-auto mt-12 max-w-md text-sm" style={{ color: "var(--muted-warm)" }}>
              {t.news.nlLabel}
            </p>
            {t.news.leadMagnet && (
              <p className="mx-auto mt-2 max-w-md text-sm font-medium" style={{ color: "var(--camel)" }} data-testid="leadmagnet-teaser">
                {t.news.leadMagnet.teaser}
              </p>
            )}
            <form onSubmit={submit} className="mx-auto mt-4 flex max-w-md flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
              <input
                data-testid="newsletter-input-email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.news.placeholder}
                className="flex-1 rounded-full border px-6 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-[var(--camel)] bg-white"
                style={{ borderColor: "var(--line)" }}
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={status === "loading"}
                className="pill-btn flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-medium text-white disabled:opacity-60"
                style={{ background: "var(--camel)" }}
              >
                {status === "loading" && <Loader2 size={15} className="animate-spin" />}
                {t.news.button}
              </button>
            </form>
            {feedback && (
              <p data-testid="newsletter-feedback" className="mt-5 text-sm font-medium" style={{ color: status === "error" ? "var(--rose)" : "var(--camel)" }}>
                {feedback}
              </p>
            )}
            {unlocked && t.news.leadMagnet && (
              <div className="mx-auto mt-6 flex max-w-md flex-col gap-3 sm:flex-row sm:justify-center" data-testid="leadmagnet-downloads">
                <button
                  type="button"
                  data-testid="leadmagnet-guide-btn"
                  onClick={() => buildStyleGuide(t)}
                  className="pill-btn flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-sm font-medium"
                  style={{ borderColor: "var(--camel)", color: "var(--camel)" }}
                >
                  <Download size={15} /> {t.news.leadMagnet.guideBtn}
                </button>
                <button
                  type="button"
                  data-testid="leadmagnet-lookbook-btn"
                  onClick={downloadLookbook}
                  disabled={busyLookbook}
                  className="pill-btn flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-sm font-medium disabled:opacity-60"
                  style={{ borderColor: "var(--camel)", color: "var(--camel)" }}
                >
                  {busyLookbook ? <Loader2 size={15} className="animate-spin" /> : <BookOpen size={15} />} {t.news.leadMagnet.lookbookBtn}
                </button>
              </div>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
